import type { QueryClient } from '@tanstack/react-query'
import type { KambiEvent, KambiSearchTermsView } from '@mobi/api-types/kambi'
import { simpleFetch } from '@mobi/utils/fetch/simpleFetch'
import { getSearchResult } from './listView'

type ResultTerm = KambiSearchTermsView['resultTerms']['0']

export async function getSportsTermSearch(
  queryClient: QueryClient,
  searchTerm: string,
  baseQueryKey: string[],
  queryCacheTime: number
) {
  const searchResults = await simpleFetch<KambiSearchTermsView>(
    `${window._kc.apiOfferingBaseUrl}term/search?lang=en_AU&market=AU&term=${searchTerm}`
  )

  const searchLower = searchTerm.toLowerCase()

  const promises = searchResults.resultTerms
    // order by most specific first
    .sort((a, b) => b.id.length - a.id.length)
    .map(async res => {
      res.value = res.localizedName

      try {
        const itemResult = await queryClient.fetchQuery({
          queryKey: [...baseQueryKey, res.id],
          queryFn: () => getSearchResult(res.type === 'REGION' ? res.parentId : res.id),
          staleTime: queryCacheTime,
          gcTime: queryCacheTime,
        })

        res.icon = itemResult.events[0]?.event.sport
        if (res.type === 'LEAGUE' || res.type === 'REGION' || res.type === 'SPORT') {
          const event =
            itemResult.events.find(x => x.event.name.toLowerCase().includes(searchLower)) ||
            itemResult.events[0]

          if (event) {
            const subheading = event.event.path
              .slice(0, event.event.path.length - 1)
              .map(x => x.name)
              .join(' - ')
            res.secondaryValue = subheading
            const g = getEventSportAndComp(event.event)
            res.sportCode = g.sportCode
            if (res.type === 'LEAGUE') {
              res.competition = g.competition
            }
          }

          return [res]
        } else if (res.type === 'PARTICIPANT') {
          const participant = res.localizedName
          const searchedEvents = itemResult.events
            .filter(
              x =>
                x.event.name.toLowerCase().includes(searchLower) ||
                x.betOffers.some(bo => bo.outcomes.some(oc => oc.participant === participant))
            )
            .map(ev => {
              const path = '/' + ev.event.path.map(x => x.termKey).join('/')
              const id = `${path}/event/${ev.event.id}?participant=${encodeURIComponent(res.localizedName)}`

              const subheading =
                ev.event.path.length > 1
                  ? `${ev.event.path[0].name} - ${ev.event.path[ev.event.path.length - 1].name} - ${participant}`
                  : `${ev.event.path[0].name} - ${participant}`

              const r: ResultTerm = {
                type: 'EVENT',
                id,
                termKey: res.termKey,
                localizedName: ev.event.name,
                parentId: path,
                englishName: ev.event.englishName,
                value: ev.event.name,
                icon: ev.event.sport,
                secondaryValue: subheading,
                ...getEventSportAndComp(ev.event),
              }
              return r
            })

          return [...searchedEvents]
        }

        return [res]
      } catch {
        // ignore it
        return [res]
      }
    })

  const res = await Promise.all(promises)

  // only show the events the participants are in, not the participants
  searchResults.resultTerms = res
    .flatMap(x => x)
    .filter(x => x && x.type !== 'PARTICIPANT') as ResultTerm[]

  // remove duplicate items
  const seenItems = new Set<string>()
  for (let index = 0; index < searchResults.resultTerms.length; ) {
    const element = searchResults.resultTerms[index]
    if (seenItems.has(element.id)) {
      searchResults.resultTerms.splice(index, 1)
      continue
    }
    seenItems.add(element.id)
    index++
  }

  return searchResults
}

function getEventSportAndComp(event: KambiEvent) {
  return {
    sportCode: event.sport,
    competition: event.path.length > 1 ? event.path[event.path.length - 1].name : undefined,
  }
}
